<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <help-link appId="finance" pageId="reimbursements-self-service" color="white"></help-link>
                    <v-btn v-if="hasAddRights" fab dark small color="primary" title="Create Reimbursements" @click="showAdd">
                        <v-icon dark>add</v-icon>
                    </v-btn>
                </v-toolbar>
            </v-card>
            <v-card class="elevation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <div :class="isMobile ? 'wp-50 mb-10' : 'wp-30 mb-10'">
                        <v-text-field v-model="search" append-icon="search" label="Search type, status" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? headers_mobile: headers"
                    :items="reimbursements_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                >
                    <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>
                    <template v-slot:items="props">
                        <tr @click="viewDetails(props.item.reimbursementsId)" title="Click to view details" class="clickable">
                            <td>{{props.item.reimbursementType}}</td>
                            <td>₱{{numFormat.formatNumber(props.item.amount)}}</td>
                            <td v-if="!isMobile">
                                {{props.item.createdOn | moment('MMM DD, YYYY')}}</span>
                            </td>
                            <td>
                                <b :class="commonfn.getStatusColor(props.item.status)">{{commonfn.getStatusDescription(props.item.status)}}</b>
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>

        <v-dialog v-model="dialogAddEdit" width="500" persistent>   
            <add-edit ref="addEditRef" :dialogAddEdit.sync="dialogAddEdit" @send="viewDetails"></add-edit>
        </v-dialog>
    </v-layout> 
</template>

<script>

import reimbursementsApi from '@/api/reimbursements'
import commonfn from '@/common/functions'
import numFormat from '@/common/num_format'
import pageKeys from '@/common/pagekeys'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getSort, getRowsPerPage, updateRowsPerPage, updateSort } from '@/helpers/user-prefs'
import { hasAddRights } from '@/helpers/access-permissions'

export default {     
    name: 'MyReimbursements',
    metaInfo: {
        title: 'My Reimbursements',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'View, create My Reimbursements' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {
        'help-link': () => import('@/components/Common/HelpLink'),
        'add-edit': () => import('@/components/Reimbursements/AddEditReimbursements')
    },   
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)

        return {
            commonfn,
            numFormat,
            rowsPerPageItems,
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            search: null,
            error: false,
            errorMessage: '',
            loading: false,
            dialogAddEdit: false,
            headers: [
                {text: 'Type',align: 'left',value: 'reimbursementType', width: '25%', sortable:true},
                {text: 'Amount',align: 'left',value: 'amount',  width: '25%', sortable:true},
                {text: 'Created On',align: 'left',value: 'createdOn',  width: '25%', sortable:true},
                {text: 'Status',align: 'left',value: 'status',  width: '25%', sortable:true},
            ],
            headers_mobile: [
                {text: 'Type',align: 'left',value: 'reimbursementType', width: '25%', sortable:true},
                {text: 'Amount',align: 'left',value: 'amount',  width: '25%', sortable:true},
                {text: 'Status',align: 'left',value: 'status',  width: '25%', sortable:true},
            ],
            reimbursements: []
        }
    },
    methods: {
        viewDetails(reimbursementsId) {
            this.$router.push(`/finance/myreimbursements/${reimbursementsId}`)
        },
        showAdd() {
            const data = {
                isRequestForOtherUser: false,
                pageKey: pageKeys.myreimbursements
            }
            this.$refs.addEditRef.setAdd(data)
            this.dialogAddEdit = true
        },
        async getMyReimbursements(show_loading) {
            try {
                this.loading = show_loading
                const response = await reimbursementsApi.getMyReimbursements()
                if (response.status == 200) {
                    this.reimbursements = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }
    },
    created() {
        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'My Reimbursements'
        })

        this.getMyReimbursements(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        reimbursements_filtered() { 
            let temp = this.reimbursements

            if (this.search) {
                temp = temp.filter(item => {
                    const searchLower = this.search.toLowerCase()
                    const typeMatch = item.reimbursementType.toLowerCase().includes(searchLower)
                    const statusMatch = commonfn.getStatusDescription(item.status).toLowerCase().includes(searchLower) 
                    return typeMatch || statusMatch
                })
            }

            // reformat to auto wrap if mobile view
            return temp.map(item => ({
                ...item,
                reimbursementType: item.reimbursementType.replace(/\//g, ' / ')
            }))
        },
        hasAddRights() {
            return hasAddRights(this.$router.currentRoute.path)
        },
    }
}
</script>